import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useLocalStorageState from 'use-local-storage-state';
import { AppContext, DEFAULT_FILTERS_BY_USER } from '../../App';
import { HorizontalDiv } from '../../components/Common';
import LoadingWheel from '../../components/LoadingWheel';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import {
    CAC_TYPE,
    MAX_NUMBER_OF_TECHFORMS,
    SORT_TYPE,
    TECHFORM_FILTER_FIELDS,
    TECHFORM_MAIN_FIELDS,
    TECHFORM_MAIN_FIELDS_KEYS
} from '../../res/Constants';
import colors from '../../res/styles/Colors';
import dimension from '../../res/styles/Responsive';
import { getFilters } from '../../service/FilterService';
import { downloadPhotosByDates } from '../../service/PhotoService';
import { downloadExcel, getTechformList, getTechformListSize } from '../../service/TechFormService';
import { toastError, toastInfo, toastSuccess } from '../../ui/ToastHelper';
import { filterByFields, sort } from '../../utils/Algorithms';
import { formatDateStringToLong } from '../../utils/DateFormatter';
import DownloadPhotoDialog from './components/DownloadPhotoDialog';
import NavBar from './components/NavBar';
import TechFilters from './components/TechFilters';
import TechList from './components/TechList';
import TooManyResultsMessage from './components/TooManyResultsMessage';

const DEFAULT_RESULTS_COUNT = -1;
const fieldsToSearchOn = [...TECHFORM_MAIN_FIELDS_KEYS, 'varietyCode', 'orderNumber', 'growerFieldName'];
const defaultSortSettings = {
    sortType: SORT_TYPE.ASCENDANT,
    field: TECHFORM_MAIN_FIELDS.GROWER
};

export default function TechFormList() {
    const history = useHistory();
    const pageWrapperRef = React.useRef(null);

    const [sortSettings, setSortSettings] = useLocalStorageState('sortSettings', {
        defaultValue: defaultSortSettings
    });
    const [techFormListFiltered, setTechFormListFiltered] = React.useState(null);
    const [canFetchTechformList, setCanFetchTechformList] = React.useState(false);
    const [rawFilters, setRawFilters] = React.useState();
    const [resetFiltersCounter, setResetFiltersCounter] = React.useState(0);
    const [resetSearchCounter, setResetSearchCounter] = React.useState(0);
    const [resultsCount, setResultsCount] = React.useState(DEFAULT_RESULTS_COUNT);
    const [photosDialogVisible, setPhotosDialogVisible] = React.useState(false);
    const [hasRestoredScroll, setHasRestoredScroll] = React.useState(false);

    const appContext = React.useContext(AppContext);
    
    // Persist hasAppliedFilters in localStorage so it survives navigation (separate for crop/nursery)
    const [hasAppliedFilters, setHasAppliedFilters] = useLocalStorageState(
        `hasAppliedFilters_${appContext.cacType}`,
        { defaultValue: false }
    );

    function getCurrentFilters() {
        return appContext.cacType === CAC_TYPE.CROP
            ? appContext.filtersByUser.crop
            : appContext.filtersByUser.nursery;
    }

    const { data: techFormList, isFetching } = useQuery({
        queryKey: [appContext.cacType, appContext.filtersByUser],
        queryFn: async () => await getTechformList(appContext.cacType, appContext.filtersByUser),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        gcTime: Infinity,
        staleTime: Infinity,
        enabled: canFetchTechformList
    });
    const shouldShowApplyFiltersMessage =
        !isFetching &&
        !hasAppliedFilters &&
        resultsCount === DEFAULT_RESULTS_COUNT &&
        !canFetchTechformList;

    // On component mount, if filters are applied and hasAppliedFilters is true, enable the query
    React.useEffect(() => {
        const currentFilters = getCurrentFilters();
        if (hasAppliedFilters && currentFilters != null && !canFetchTechformList && techFormList == null) {
            // Filters were applied in a previous session, re-enable the query to load from cache
            setCanFetchTechformList(true);
        }
    }, []);

    React.useEffect(() => {
        if (techFormList != null) {
            setCanFetchTechformList(false);
            setResultsCount(DEFAULT_RESULTS_COUNT);
        }
    }, [techFormList]);

    const sortTechFormList = (list, field, sortType) => {
        const indexValue = TECHFORM_FILTER_FIELDS.findIndex((e) => e === field.value);
        if (indexValue === -1) {
            return list;
        }
        const propToSort = TECHFORM_MAIN_FIELDS_KEYS[indexValue];
        return sort(list, sortType, propToSort);
    };

    React.useEffect(() => {
        setTechFormListFiltered(null);
        setResultsCount(DEFAULT_RESULTS_COUNT);
    }, [appContext.cacType]);

    React.useEffect(() => {
        if (techFormList != null && techFormList.length > 0) {
            const { field, sortType } = sortSettings;
            setTechFormListFiltered(sortTechFormList(techFormList, field, sortType));
        }
    }, [sortSettings, techFormList]);

    React.useEffect(() => {
        getFilters(appContext.cacType, (fetchedFilters) => {
            if (fetchedFilters == null) return;
            setRawFilters(fetchedFilters);
        });
    }, [appContext.cacType]);

    const onLoadTechformList = () => {
        setCanFetchTechformList(true);
        setResultsCount(DEFAULT_RESULTS_COUNT);
    };

    const onFiltersApply = async (newFilters) => {
        const filterKeyToUpdate = appContext.cacType === CAC_TYPE.CROP ? 'crop' : 'nursery';
        appContext.setFiltersByUser((curr) => ({
            ...curr,
            [filterKeyToUpdate]: newFilters
        }));
        setTechFormListFiltered(null);
        setHasAppliedFilters(true);

        const resultsSize = await getTechformListSize(newFilters);
        console.log('TCL: onFiltersApply -> resultsSize', resultsSize);
        setResultsCount(resultsSize);
        if (resultsSize > MAX_NUMBER_OF_TECHFORMS || resultsSize === 0) {
            setCanFetchTechformList(false);
        } else if (resultsSize > 0) {
            onLoadTechformList();
        }
    };

    const onExcelDownload = (lines) => {
        downloadExcel(
            { ...getCurrentFilters(), type: appContext.cacType },
            lines,
            () => { },
            () => { }
        );
    };

    const onPhotosDownload = (from, to) => {
        setPhotosDialogVisible(false);
        const fromAsLong = formatDateStringToLong(from);
        const toAsLong = formatDateStringToLong(to);
        const persistentMessage = (
            <LoadingToastMessage text={'Downloading photos has started, please wait.'} />
        );
        toastInfo(persistentMessage, true);
        downloadPhotosByDates(
            { ...getCurrentFilters(), type: appContext.cacType },
            fromAsLong,
            toAsLong,
            () => toastSuccess('Download completed.'),
            (error) => toastError(error || 'Server error.')
        );
    };

    const onSearchChange = (value) => {
        const techFormsFilteredBySearch = filterByFields(techFormList, fieldsToSearchOn, value);
        setTechFormListFiltered(techFormsFilteredBySearch);
    };

    const onSortingSettingsChange = (newSortSettings) => {
        setSortSettings(newSortSettings);
    };

    const onTechFormClick = (id) => {
        const currentScrollTop = pageWrapperRef.current?.scrollTop || 0;
        const nextState = {
            ...(history.location.state || {}),
            techFormListScroll: currentScrollTop
        };
        history.replace({ ...history.location, state: nextState });
        history.push(`/techforms/${id}`);
    };

    const onFiltersReset = () => {
        // setTechFormList(null);
        setTechFormListFiltered(null);
        setHasAppliedFilters(false);
        setResultsCount(DEFAULT_RESULTS_COUNT);
        appContext.setFiltersByUser(DEFAULT_FILTERS_BY_USER);
        setSortSettings(defaultSortSettings);
        setResetFiltersCounter(resetFiltersCounter + 1);
        setResetSearchCounter(resetSearchCounter + 1);
    };

    const techFormListToDisplay = techFormListFiltered || techFormList;
    const savedScrollPosition = history.location.state?.techFormListScroll;

    React.useLayoutEffect(() => {
        if (hasRestoredScroll) return;
        if (savedScrollPosition == null) return;
        if (!techFormListToDisplay || techFormListToDisplay.length === 0) return;
        if (pageWrapperRef.current) {
            pageWrapperRef.current.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
            setHasRestoredScroll(true);
        }
    }, [hasRestoredScroll, savedScrollPosition, techFormListToDisplay]);

    return (
        <PageWrapper ref={pageWrapperRef}>
            <NavigationBar />
            <NavBar
                filters={rawFilters}
                prevFilters={getCurrentFilters()}
                fieldToSortBy={sortSettings}
                downloadEnabled={hasAppliedFilters && techFormListToDisplay?.length > 0}
                onExcelDownload={onExcelDownload}
                onPhotosDownload={() => setPhotosDialogVisible(true)}
                onSortSettingsChange={onSortingSettingsChange}
                onFiltersApply={onFiltersApply}
                onSearchChange={onSearchChange}
                onFiltersReset={onFiltersReset}
                searchKey={resetSearchCounter}
                key={resetFiltersCounter}
            />
            <TechFormPage>
                {!isMobileOnly && rawFilters != null && (
                    <TechFilters
                        filters={rawFilters}
                        prevFilters={getCurrentFilters()}
                        onFiltersApply={onFiltersApply}
                        onFiltersReset={onFiltersReset}
                        searchKey={resetSearchCounter}
                        key={`${resetFiltersCounter}-${appContext.cacType}`}
                    />
                )}
                {isFetching && <LoadingWheel />}
                {!isFetching && resultsCount === 0 && (
                    <StyledEmptyError>No results for current filters</StyledEmptyError>
                )}
                {shouldShowApplyFiltersMessage && (
                    <StyledEmptyError>Please apply filters to see the results</StyledEmptyError>
                )}
                {!isFetching && hasAppliedFilters && techFormListToDisplay?.length > 0 && (
                    <TechList techFormList={techFormListToDisplay} onItemClick={onTechFormClick} />
                )}
                {!isFetching && resultsCount > MAX_NUMBER_OF_TECHFORMS && (
                    <TooManyResultsMessage
                        cacType={appContext.cacType}
                        resultsCount={resultsCount}
                        onContinue={onLoadTechformList}
                        onExcelDownload={onExcelDownload}
                        onPhotosDownload={() => setPhotosDialogVisible(true)}
                    />
                )}
                <DownloadPhotoDialog
                    isModalOpen={photosDialogVisible}
                    toggleDialog={() => setPhotosDialogVisible((curr) => !curr)}
                    onPhotosDownload={onPhotosDownload}
                />
            </TechFormPage>
        </PageWrapper>
    );
}

function LoadingToastMessage(props) {
    return (
        <HorizontalDiv>
            <p style={{ margin: '0 20px 0 5px' }}>{props.text}</p>
            <LoadingWheel height={30} width={30} />
        </HorizontalDiv>
    );
}

const PageWrapper = styled.div`
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
`;

const TechFormPage = styled.div`
    min-height: 74vh;
    padding-top: 0px;
    background-color: ${colors.light_gray};

    @media ${dimension.md} {
        display: flex;
        min-height: 81vh;
    }
`;

const StyledEmptyError = styled.p`
    color: ${colors.red};
    margin: auto;
    text-align: center;

    @media ${dimension.md} {
        margin-top: 30px;
    }
`;
